import {
  ArrowBackIosNew,
  ArrowForwardIos,
  Close,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import { Box, createTheme, IconButton, ThemeProvider } from '@mui/material';
import { useDialog } from '@prismamedia/one-components';
import { FC, useState } from 'react';
import {
  ReactZoomPanPinchContentRef,
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { theme } from 'theme';
import { FullscreenRailroadMiniature } from './FullscreenRailroadMiniature';
import { RailroadIssueFullPage } from './RailroadIssueFullPage';
import { RailroadFullPage } from './utils';

export const FullscreenRailroad: FC<{
  pages: RailroadFullPage[];
  startingIndex?: number;
}> = ({ pages, startingIndex }) => {
  const { closeDialog } = useDialog();
  const [pinchState, setPinchState] = useState<
    ReactZoomPanPinchRef | undefined
  >();
  const [selectedPage, setSelectedPage] = useState(startingIndex || 0);

  return (
    <Box
      sx={{
        height: '100vh',
        bgcolor: theme.palette.grey[800],
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          height: 'calc(100% - 150px)',
        }}
      >
        <TransformWrapper
          doubleClick={{ step: 1.5 }}
          pinch={{ disabled: false, step: 1.5 }}
          wheel={{ disabled: false, step: 1.5 }}
          zoomAnimation={{ size: 10 }}
          minScale={0.3}
          maxScale={10}
          limitToBounds
          centerZoomedOut
          initialScale={1}
          centerOnInit
          onTransformed={(ref) => setPinchState(ref)}
        >
          {({ zoomIn, zoomOut, instance }: ReactZoomPanPinchContentRef) => {
            return (
              <>
                <TransformComponent
                  wrapperStyle={{ width: '100%', height: '100%' }}
                >
                  <RailroadIssueFullPage
                    pages={pages}
                    pageIndex={selectedPage}
                    displayOptions={{
                      displayBorders: false,
                      displayThumbnails: true,
                      size: 0,
                      miniature: false,
                      displayInfos: false,
                      displayPageNumber: false,
                    }}
                    containerSx={{
                      maxWidth: '40vw',
                    }}
                  />
                </TransformComponent>
                <ThemeProvider
                  theme={createTheme({ palette: { mode: 'dark' } })}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      transform: 'translateX(-50%)',
                      left: '50%',
                    }}
                  >
                    <IconButton
                      sx={{
                        mx: 1,
                        bgcolor: theme.palette.common.black,
                        ':hover': {
                          bgcolor: theme.palette.grey['600'],
                        },
                      }}
                      onClick={() => zoomOut(1)}
                      disabled={
                        Number(pinchState?.state.scale) <=
                        Number(instance.props.minScale)
                      }
                    >
                      <ZoomOut />
                    </IconButton>
                    <IconButton
                      sx={{
                        mx: 1,
                        bgcolor: theme.palette.common.black,
                        ':hover': {
                          bgcolor: theme.palette.grey['600'],
                        },
                      }}
                      onClick={() => zoomIn(1)}
                      disabled={
                        Number(pinchState?.state.scale) >=
                        Number(instance.props.maxScale)
                      }
                    >
                      <ZoomIn />
                    </IconButton>
                  </Box>
                </ThemeProvider>
              </>
            );
          }}
        </TransformWrapper>
        <IconButton
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: 8,
            color: theme.palette.common.white,
            bgcolor: theme.palette.common.black,
            ':hover': {
              bgcolor: theme.palette.grey['600'],
            },
          }}
          disabled={selectedPage === 0}
          onClick={() => setSelectedPage((prev) => prev - 1)}
        >
          <ArrowBackIosNew />
        </IconButton>
        <IconButton
          sx={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: 8,
            color: theme.palette.common.white,
            bgcolor: theme.palette.common.black,
            ':hover': {
              bgcolor: theme.palette.grey['600'],
            },
          }}
          disabled={selectedPage === pages.length - 1}
          onClick={() => setSelectedPage((prev) => prev + 1)}
        >
          <ArrowForwardIos />
        </IconButton>
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: theme.palette.common.white,
            bgcolor: theme.palette.common.black,
            ':hover': {
              bgcolor: theme.palette.grey['600'],
            },
          }}
          onClick={() => closeDialog()}
        >
          <Close />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          p: 1,
          flexShrink: 0,
          bgcolor: theme.palette.common.white,
          maxWidth: '100%',
          height: '150px',
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        {pages.map((_, i) => (
          <FullscreenRailroadMiniature
            key={i}
            pages={pages}
            pageIndex={i}
            sx={{
              width: '150px',
            }}
            selected={selectedPage === i}
            onSelect={() => setSelectedPage(i)}
          />
        ))}
      </Box>
    </Box>
  );
};
